import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import styles from './index.module.scss';
import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/i18n';
import EcoButton from 'eco-design/base/eco-button';
import BonusModal, { BonusModalHandle } from '../../../Share/MyBonus/BonusModal';
import RewardsModal, { RewardsModalHandle } from '../../../Share/RewardsModal';
import { useReferralDispatch, useReferralStore } from '../../../ReferralProvider';
import { formatPrice } from '../../../util';
import { withdrawalWhiteLocale } from '@/constants/my';
import { QuestionIcon2 } from '@/components/UI/svg/Referral';
import gtm from '@/utils/gtm';
import EcoImg from 'eco-design/base/eco-img';

export default function RewardsBox() {
    const { t } = useLang('referral');
    const { locale } = getLocal();
    const [disabled, setDisabled] = useState(true);

    const bonusRef = useRef<BonusModalHandle>(null);
    const rewardsRef = useRef<RewardsModalHandle>(null);
    const dispatch = useReferralDispatch();
    const { userPointsInfo, referralInfo, userWalletInfo, inBlackList } = useReferralStore();

    const showWithdrawal = withdrawalWhiteLocale.includes(locale);

    const nums = [
        {
            num: userPointsInfo?.registerCount || 0,
            text: t('mybonus.ecoCredits1'),
        },
        {
            num: userPointsInfo?.purchaseCount || 0,
            text: t('mybonus.ecoCredits2'),
        },
        ...(showWithdrawal
            ? [
                  {
                      num: formatPrice(locale, userWalletInfo?.availableAmount || 0),
                      text: t('mybonus.ecoCredits3'),
                      hasTooltip: true,
                  },
              ]
            : []),
    ];

    useEffect(() => {
        if (!referralInfo) return;
        dispatch({ type: 'getUserPointsInfo', payload: { locale, planId: referralInfo?.planId } });
        dispatch({ type: 'getUserWalletInfo', payload: { locale } });
    }, [referralInfo]);

    useEffect(() => {
        if (!userWalletInfo) return;
        if (userWalletInfo?.availableAmount >= userWalletInfo?.withdrawalMinAmount && !inBlackList) {
            setDisabled(false);
        }
    }, [userWalletInfo]);

    useEffect(() => {
        const handleStorageChange = (event: any) => {
            if (event.key === 'withdrawalrequest-close') {
                dispatch({ type: 'getUserWalletInfo', payload: { locale } });
            }
        };

        const detectCloseHandler = () => {
            window.addEventListener('storage', handleStorageChange, false);
        };

        detectCloseHandler();

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    return (
        <div className={`${styles.MyRewards} GtmModuleData-section`}>
            <div className="sh-layout">
                <div className="rewards-box">
                    <EcoImg
                        pc="https://websiteoss.ecoflow.com/figma-uploads/1767493585026-su1o4u-compressed-Frame%20128720172.jpg"
                        pad="https://websiteoss.ecoflow.com/figma-uploads/1767494083932-br6cli-compressed-Frame%20128721320.jpg"
                    />
                    <div className="nums-box">
                        {nums.map((item, index) => (
                            <>
                                <div className="num-item" key={`item-${index}`}>
                                    <div className="num">{item.num}</div>
                                    <div className="text">
                                        {item.text}
                                        {item.hasTooltip && (
                                            <Tooltip
                                                placement="top"
                                                trigger={['hover', 'click']}
                                                title={t('mybonus.ecoCredits3_tips', {
                                                    code: formatPrice(locale, userWalletInfo?.withdrawalMinAmount || 0, false),
                                                })}
                                                color="#656565"
                                            >
                                                <QuestionIcon2 className="qus_icon" />
                                            </Tooltip>
                                        )}
                                    </div>
                                    {item.hasTooltip && (
                                        <div className="withdrawal-link">
                                            {!disabled ? (
                                                <a
                                                    href={`/${locale}/withdrawal-request?redirect=${encodeURIComponent(
                                                        typeof window !== 'undefined' ? location.href : '',
                                                    )}`}
                                                    className="link"
                                                    target={['br', 'mx'].includes(locale) ? '_self' : '_blank'}
                                                    rel="noopener noreferrer"
                                                >
                                                    {t('mybonus.ecoCredits3_btn')} &gt;
                                                </a>
                                            ) : (
                                                <span className="disabled link">{t('mybonus.ecoCredits3_btn')} &gt;</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {index !== nums.length - 1 && <div className="separator" key={`separator-${index}`}></div>}
                            </>
                        ))}
                    </div>
                    <div className="btn-box">
                        <EcoButton
                            lRadius={'ellipse'}
                            lTheme="black"
                            onClick={() => {
                                rewardsRef.current?.show();
                                try {
                                    gtm.push({
                                        event: 'uaEvent',
                                        event_name: 'referral_button',
                                        position: referralInfo?.title || '',
                                        button_name: t('banner.btn2'),
                                    });
                                } catch (e) {
                                    console.log(e);
                                }
                            }}
                        >
                            {t('banner.btn2')}
                        </EcoButton>
                        <EcoButton
                            lRadius={'ellipse'}
                            lTheme="black"
                            onClick={() => {
                                bonusRef.current?.show();
                                try {
                                    gtm.push({
                                        event: 'uaEvent',
                                        event_name: 'referral_button',
                                        position: referralInfo?.title || '',
                                        button_name: t('mybonus.btn'),
                                    });
                                } catch (e) {
                                    console.log(e);
                                }
                            }}
                        >
                            {t('mybonus.btn')}
                        </EcoButton>
                    </div>
                </div>
            </div>
            <BonusModal ref={bonusRef} planId={referralInfo?.planId} />
            <RewardsModal ref={rewardsRef} planId={referralInfo?.planId} />
        </div>
    );
}
